import { useNavigate } from 'react-router-dom'
import CheckpointVideo from '../components/progress/CheckpointVideo'
import { useHomeData } from '../hooks/home/useHomeData'
import { useActiveGate } from '../hooks/gate/useActiveGate'
import { useGates } from '../hooks/gate/useGates'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { profilePath, ScreenPath } from '../navigation'
import AppShell from '../ui/layout/AppShell'
import PageHeaderContainer from '../components/layout/PageHeaderContainer'
import ActivityFeed from '../ui/home/ActivityFeed'
import AnnouncementBanner from '../ui/home/AnnouncementBanner'
import BucketGrid from '../ui/home/BucketGrid'
import { getCurrentCheckpoint } from '../logic/checkpointUtils'
import CheckpointCard from '../ui/home/CheckpointCard'
import MemberCard from '../ui/home/MemberCard'
import ProgressHero from '../ui/home/ProgressHero'
import RestDayPill from '../ui/home/RestDayPill'
import HorizontalScroll from '../ui/primitives/HorizontalScroll'
import SectionLabel from '../ui/primitives/SectionLabel'

export default function HomeScreen() {
  const data = useHomeData()
  const { data: activeGate } = useActiveGate()
  const { data: gates } = useGates()
  const { isFreeAgent } = useChallengeAccess()
  const navigate = useNavigate()

  const currentCheckpoint = data
    ? getCurrentCheckpoint(data.checkpoints, activeGate?.checkpoint.id)
    : undefined

  if (!data) {
    return (
      <AppShell header={<PageHeaderContainer />}>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>
          {isFreeAgent ? 'Join a team to see progress here.' : 'Loading…'}
        </p>
      </AppShell>
    )
  }

  return (
    <AppShell header={<PageHeaderContainer />}>
      {data.announcement && (
        <AnnouncementBanner
          id={data.announcement.id}
          title={data.announcement.title}
          link={data.announcement.link}
          videoUrl={data.announcement.videoUrl}
        />
      )}

      <ProgressHero
        currentDay={data.currentDay}
        totalDays={data.totalDays}
        teamProgress={data.teamProgress}
        goal={data.goal}
        progressPercent={data.progressPercent}
        paceProgress={data.paceProgress}
        paceDelta={data.paceDelta}
        dailyPace={data.dailyPace}
        progressType={data.progressType}
        checkpoints={data.checkpoints}
        currentCheckpointId={currentCheckpoint?.id}
        routeCoordinates={data.routeCoordinates}
        gateMarks={data.gateMarks}
      />

      <SectionLabel large>Gates</SectionLabel>
      <HorizontalScroll
        variant="wide"
        scrollToSelector="[data-current]"
        scrollKey={currentCheckpoint?.id}
      >
        {data.checkpoints.map((cp) => {
          const gate = gates?.find((candidate) => candidate.checkpointID === cp.id)
          if (!gate) return null
          const isGated = activeGate?.gate.id === gate.id
          const canOpen = isGated || cp.status === 'done'
          return (
            <CheckpointCard
              key={cp.id}
              checkpoint={cp}
              gated={isGated}
              current={cp.id === currentCheckpoint?.id}
              onClick={
                canOpen ? () => navigate(`${ScreenPath.gate}/${gate.id}`) : undefined
              }
            />
          )
        })}
      </HorizontalScroll>

      <SectionLabel large>Buckets</SectionLabel>
      <BucketGrid buckets={data.buckets} />

      {data.restDaysTotal > 0 && (
        <RestDayPill
          remaining={data.restDaysRemaining}
          total={data.restDaysTotal}
          used={data.restDaysUsed}
        />
      )}

      <SectionLabel>Individual Progress</SectionLabel>
      <HorizontalScroll variant="member">
        {data.members.map((member) => (
          <MemberCard
            key={member.user.id}
            member={member}
            onClick={() => navigate(profilePath(member.user.id))}
          />
        ))}
      </HorizontalScroll>

      <SectionLabel>Recent Team Activity</SectionLabel>
      <ActivityFeed items={data.feed} />
      <CheckpointVideo />
    </AppShell>
  )
}
